/**
 * Lambda User Data Service
 * Provides authenticated user-specific data access to RDS via Lambda
 * Handles profiles, addresses, and user roles through AWS Lambda API
 */
import { logger } from '@/utils/logger';
import { Tables } from '@/integrations/supabase/types';

export interface UserProfile {
  id: string;
  full_name: string;
  phone: string;
  email: string;
  country: string;
  preferred_display_currency: string;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: string;
  is_active: boolean;
  granted_at: string;
  granted_by: string;
}

export interface LambdaUserResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  source: string;
  auth?: {
    role: string;
    authenticated: boolean;
  };
}

class LambdaUserDataService {
  private static instance: LambdaUserDataService;
  private baseURL: string;

  private constructor() {
    // Use the dedicated user API URL for authenticated endpoints
    this.baseURL = import.meta.env.VITE_AWS_USER_API_URL || 'https://api.iwishbag.com/user';
    logger.info('[LambdaUserDataService] Initialized with User API Gateway URL:', this.baseURL);
  }

  static getInstance(): LambdaUserDataService {
    if (!LambdaUserDataService.instance) {
      LambdaUserDataService.instance = new LambdaUserDataService();
    }
    return LambdaUserDataService.instance;
  }

  /**
   * Get authentication headers for requests
   */
  private getAuthHeaders(authToken?: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;

      // Debug JWT token structure
      try {
        // Decode token without verification to debug structure
        const tokenParts = authToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('🔍 [AUTH DEBUG] JWT token info:', {
            issuer: payload.iss,
            audience: payload.aud,
            subject: payload.sub,
            role: payload.role,
            email: payload.email,
            expiresAt: new Date(payload.exp * 1000).toISOString(),
            isExpired: payload.exp < Date.now() / 1000
          });
        }
      } catch (error) {
        console.warn('🔍 [AUTH DEBUG] Failed to decode JWT token:', error.message);
      }
    } else {
      console.warn('🔍 [AUTH DEBUG] No auth token provided for request');
    }

    return headers;
  }

  /**
   * Handle API response and error checking
   */
  private async handleResponse<T>(response: Response): Promise<LambdaUserResponse<T>> {
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;

      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If error response is not JSON, use the text as is
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  /**
   * Get user profile data
   */
  async getUserProfile(userId: string, authToken: string): Promise<LambdaUserResponse<UserProfile>> {
    try {
      logger.info('[LambdaUserDataService] Fetching user profile for:', userId);

      const response = await fetch(`${this.baseURL}/profile`, {
        method: 'GET',
        headers: this.getAuthHeaders(authToken),
      });

      const data = await this.handleResponse<UserProfile>(response);
      logger.info('[LambdaUserDataService] Successfully fetched user profile');
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile data
   */
  async updateUserProfile(
    userId: string,
    profileData: Partial<Pick<UserProfile, 'full_name' | 'phone' | 'country' | 'preferred_display_currency'>>,
    authToken: string
  ): Promise<LambdaUserResponse<UserProfile>> {
    try {
      logger.info('[LambdaUserDataService] Updating user profile for:', userId);

      const response = await fetch(`${this.baseURL}/profile`, {
        method: 'PUT',
        headers: this.getAuthHeaders(authToken),
        body: JSON.stringify(profileData),
      });

      const data = await this.handleResponse<UserProfile>(response);
      logger.info('[LambdaUserDataService] Successfully updated user profile');
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Get user delivery addresses
   */
  async getUserAddresses(userId: string, authToken: string): Promise<LambdaUserResponse<Tables<'delivery_addresses'>[]>> {
    try {
      logger.info('[LambdaUserDataService] Fetching user addresses for:', userId);
      console.log('🔍 [API DEBUG] Making request to:', `${this.baseURL}/addresses`);
      console.log('🔍 [API DEBUG] User ID:', userId);

      const response = await fetch(`${this.baseURL}/addresses`, {
        method: 'GET',
        headers: this.getAuthHeaders(authToken),
      });

      console.log('🔍 [API DEBUG] Response status:', response.status);
      console.log('🔍 [API DEBUG] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 [API DEBUG] Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await this.handleResponse<Tables<'delivery_addresses'>[]>(response);
      logger.info('[LambdaUserDataService] Successfully fetched user addresses, count:', data.count);
      console.log('🔍 [API DEBUG] Success response:', data);
      return data;
    } catch (error) {
      const errorDetails = {
        message: error.message,
        stack: error.stack,
        url: `${this.baseURL}/addresses`,
        userId
      };
      logger.error('[LambdaUserDataService] Error fetching user addresses:', errorDetails);
      console.error('🔍 [API DEBUG] Request failed:', errorDetails);
      throw error;
    }
  }

  /**
   * Create new user address
   */
  async createUserAddress(
    userId: string,
    addressData: Omit<Tables<'delivery_addresses'>, 'id' | 'created_at' | 'updated_at' | 'user_id'>,
    authToken: string
  ): Promise<LambdaUserResponse<Tables<'delivery_addresses'>>> {
    try {
      logger.info('[LambdaUserDataService] Creating user address for:', userId);

      const response = await fetch(`${this.baseURL}/addresses`, {
        method: 'POST',
        headers: this.getAuthHeaders(authToken),
        body: JSON.stringify(addressData),
      });

      const data = await this.handleResponse<Tables<'delivery_addresses'>>(response);
      logger.info('[LambdaUserDataService] Successfully created user address');
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error creating user address:', error);
      throw error;
    }
  }

  /**
   * Update existing user address
   */
  async updateUserAddress(
    userId: string,
    addressId: string,
    addressData: Partial<Omit<Tables<'delivery_addresses'>, 'id' | 'created_at' | 'updated_at' | 'user_id'>>,
    authToken: string
  ): Promise<LambdaUserResponse<Tables<'delivery_addresses'>>> {
    try {
      logger.info('[LambdaUserDataService] Updating user address:', addressId, 'for user:', userId);

      const response = await fetch(`${this.baseURL}/addresses?id=${addressId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(authToken),
        body: JSON.stringify(addressData),
      });

      const data = await this.handleResponse<Tables<'delivery_addresses'>>(response);
      logger.info('[LambdaUserDataService] Successfully updated user address');
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error updating user address:', error);
      throw error;
    }
  }

  /**
   * Delete user address
   */
  async deleteUserAddress(
    userId: string,
    addressId: string,
    authToken: string
  ): Promise<LambdaUserResponse<{ id: string }>> {
    try {
      logger.info('[LambdaUserDataService] Deleting user address:', addressId, 'for user:', userId);

      const response = await fetch(`${this.baseURL}/addresses?id=${addressId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(authToken),
      });

      const data = await this.handleResponse<{ id: string }>(response);
      logger.info('[LambdaUserDataService] Successfully deleted user address');
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error deleting user address:', error);
      throw error;
    }
  }

  /**
   * Set address as default
   */
  async setDefaultAddress(
    userId: string,
    addressId: string,
    authToken: string
  ): Promise<LambdaUserResponse<Tables<'delivery_addresses'>>> {
    try {
      logger.info('[LambdaUserDataService] Setting default address:', addressId, 'for user:', userId);

      const response = await fetch(`${this.baseURL}/addresses?id=${addressId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(authToken),
        body: JSON.stringify({ is_default: true }),
      });

      const data = await this.handleResponse<Tables<'delivery_addresses'>>(response);
      logger.info('[LambdaUserDataService] Successfully set default address');
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error setting default address:', error);
      throw error;
    }
  }

  /**
   * Get user roles
   */
  async getUserRoles(userId: string, authToken: string): Promise<LambdaUserResponse<UserRole[]>> {
    try {
      logger.info('[LambdaUserDataService] Fetching user roles for:', userId);

      const response = await fetch(`${this.baseURL}/roles`, {
        method: 'GET',
        headers: this.getAuthHeaders(authToken),
      });

      const data = await this.handleResponse<UserRole[]>(response);
      logger.info('[LambdaUserDataService] Successfully fetched user roles, count:', data.count);
      return data;
    } catch (error) {
      logger.error('[LambdaUserDataService] Error fetching user roles:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const lambdaUserDataService = LambdaUserDataService.getInstance();

// Export type for the service
export type { LambdaUserDataService };